import React, { FC } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import createPalette from "@mui/material/styles/createPalette";
import { ThemeProvider, createTheme } from "@mui/material";
import { vars } from "../Variables";
import { useField } from "formik";
const { primaryFont, primaryColor, errorColor, greyLight, grey, greyDark } =
  vars;

declare module "@mui/material/styles" {
  interface Theme {}
  // allow configuration using `createTheme`
  interface ThemeOptions {}
}

const customPalette = createPalette({
  primary: {
    main: primaryColor,
  },
  grey: {
    "100": greyLight,
    "200": grey,
    "300": greyDark,
  },
  error: {
    main: errorColor,
  },
});

const theme = createTheme({
  typography: {
    fontFamily: primaryFont,
  },
  components: {
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: "183%",
          color: customPalette.grey[300],
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {},
        root: {
          alignItems: "flex-start",
          "&:hover:not(.Mui-disabled, .Mui-error)::before": {
            borderColor: customPalette.grey[200],
            borderWidth: 1,
          },

          "&:after": {
            borderColor: customPalette.primary.main,
          },
          "&:before": {
            borderColor: customPalette.grey[200],
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        endAdornment: {
          top: 0,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: customPalette.error.main,
        },
        root: {
          fontWeight: 400,
          fontSize: "1rem",
          color: customPalette?.grey[100],

          "&.Mui-focused": {
            color: customPalette?.grey[100],
          },
        },
      },
    },
  },
});

type itemObject = {
  id?: string;
  value: any;
  text: any;
};

interface ISelectBox {
  onChange?: (value: any) => void;
  onBlur?: (value: any) => void;
  label: string;
  id?: string;
  value?: any;
  items?: itemObject[];
  sx?: Object;
  fullWidth?: boolean;
  required?: boolean;
  name?: string;
  isMulti?: boolean;
  values?: string | string[];
  placeholder?: string;
  maxHeight?: number;
  error?: boolean;
  disabled?: boolean;
  helperText?: string;
  freeSolo?: boolean;
  inputProps?: TextFieldProps;
  valueModifier?: (value: Array<string> | string) => Array<string> | string;
}

export const SelectBox: FC<ISelectBox> = ({
  label,
  items,
  sx,
  fullWidth = true,
  required = false,
  name = "select",
  isMulti,
  onChange,
  onBlur,
  values,
  placeholder = "",
  error,
  helperText,
  disabled,
  maxHeight = 400,
  freeSolo,
  valueModifier,
  inputProps,
}) => {
  const [field, meta, helpers] = useField({
    name,
    type: "select",
    multiple: isMulti,
  });

  const onValueChange = (
    _: React.SyntheticEvent<Element, Event>,
    newValue: string | itemObject | (string | itemObject)[] | null
  ) => {
    let value = Array.isArray(newValue)
      ? newValue.length
        ? newValue?.map?.((item) =>
            typeof item === "string" ? item : item.value
          )
        : newValue
      : typeof newValue === "string"
      ? newValue
      : newValue?.value;

    if (valueModifier && value) {
      value = valueModifier(value);
    }

    helpers.setTouched(true);
    helpers.setValue(value || "", true);
    onChange?.(value || "");
  };

  const inputValue: any = React.useMemo(() => {
    const value = ![undefined, null].includes(field.value)
      ? Array.isArray(field.value)
        ? field.value.map((i) => `${i}`)
        : `${field.value}`
      : field.value;
    // In case the Input is freeSolo convert any string value to array of string.
    if (freeSolo) {
      return !Array.isArray(value) ? value && value?.split(",") : value;
    }

    // In case of isMulti get the values from items.
    if (!freeSolo && isMulti) {
      if (Array.isArray(value)) {
        return items?.filter((item) => value?.includes(`${item.value}`));
      }
      return items?.find((item) => value === `${item.value}`) || value;
    }

    // In case of normal input get the single value from item.
    return items?.find((item) => value === `${item.value}`) || value;
  }, [field.value, freeSolo, isMulti, items]);

  return (
    <ThemeProvider theme={theme}>
      <FormControl
        required={required}
        sx={sx}
        variant="standard"
        fullWidth={fullWidth}
      >
        <Autocomplete
          {...field}
          multiple={isMulti || freeSolo}
          options={items || []}
          autoSelect={freeSolo}
          ListboxProps={{
            style: {
              maxHeight: maxHeight,
            },
          }}
          value={inputValue}
          disabled={disabled}
          freeSolo={freeSolo || false}
          onChange={onValueChange}
          // defaultValue={f}
          getOptionLabel={(option: string | itemObject) => {
            if (typeof option === "string") {
              return option;
            }
            return option.text;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              {...inputProps}
              variant="standard"
              label={label}
              required={required}
              error={error || (meta.touched && !!meta.error)}
              name={field.name}
              helperText={helperText || (meta.touched ? meta.error : "")}
              fullWidth={fullWidth}
              placeholder={placeholder}
            />
          )}
        />
      </FormControl>
    </ThemeProvider>
  );
};
